// "Add to ticklist" control for a route page. Anonymous users see the button
// but get bounced to /login on click rather than having it hidden — same
// browse-everything philosophy as LoginGate, just inline.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Star } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  DELETE_TICK_FOR_ROUTE,
  MY_TICK_FOR_ROUTE,
  UPSERT_TICK,
  type MyTickForRouteResult,
} from "../lib/queries";
import { TICK_PRIORITIES, type TickPriority } from "../lib/format";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, TextArea, TextInput } from "./ui";

export function TickButton({ routeId }: { routeId: string }) {
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ["tick", personId, routeId],
    // RLS makes this pointless when logged out — it would always be null.
    enabled: personId != null,
    queryFn: () => gqlRequest<MyTickForRouteResult>(MY_TICK_FOR_ROUTE, { personId, routeId }),
  });
  const tick = data?.routeTickByPersonIdAndRouteId ?? null;
  // Don't offer the form until we know whether a tick already exists — the
  // popover seeds its fields once, on mount, from whatever we pass here.
  const loading = personId != null && isPending;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["tick", personId, routeId] });
    queryClient.invalidateQueries({ queryKey: ["ticklist", personId] });
  };

  const save = useMutation({
    // isPublic isn't editable here — pass the current value through so an
    // upsert over an existing tick doesn't flip it back to private.
    mutationFn: (vars: { priority: TickPriority; note: string | null; targetSeason: string | null }) =>
      gqlRequest(UPSERT_TICK, { personId, routeId, isPublic: tick?.isPublic ?? false, ...vars }),
    onSuccess: () => {
      setOpen(false);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: () => gqlRequest(DELETE_TICK_FOR_ROUTE, { personId, routeId }),
    onSuccess: () => {
      setOpen(false);
      invalidate();
    },
  });

  if (!personId) {
    return (
      <Button
        type="button"
        onClick={() => navigate(`/login?next=${encodeURIComponent(`/route/${routeId}`)}`)}
        className="border-alpine-200 bg-alpine-50 text-alpine-700 shadow-card hover:border-alpine-300 hover:bg-alpine-100 dark:border-alpine-900 dark:bg-alpine-950 dark:text-alpine-300 dark:hover:border-alpine-800 dark:hover:bg-alpine-900"
        variant="outline"
      >
        <Star aria-hidden className="size-4" />
        {t("tick.button")}
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          disabled={loading}
          variant="outline"
          className={
            tick
              ? "border-alpine-200 bg-alpine-50 text-alpine-700 shadow-card hover:border-alpine-300 hover:bg-alpine-100 dark:border-alpine-900 dark:bg-alpine-950 dark:text-alpine-300 dark:hover:border-alpine-800 dark:hover:bg-alpine-900"
              : "shadow-card"
          }
        >
          <Star aria-hidden className="size-4" fill={tick ? "currentColor" : "none"} />
          {tick ? labelizeT(tick.priority) : t("tick.button")}
        </Button>
      </PopoverTrigger>
      {!loading && (
        <PopoverContent
          // Keyed on the loaded row so the form remounts (and re-seeds its
          // state) if the query resolves after the popover is already open.
          key={tick?.id ?? "new"}
          align="end"
          className="w-72 shadow-pop"
        >
          <TickForm
            tick={tick}
            onSave={(vars) => save.mutate(vars)}
            onRemove={() => remove.mutate()}
            saving={save.isPending || remove.isPending}
            error={save.error ?? remove.error}
          />
        </PopoverContent>
      )}
    </Popover>
  );
}

function TickForm({
  tick,
  onSave,
  onRemove,
  saving,
  error,
}: {
  tick: { priority: TickPriority; note: string | null; targetSeason: string | null } | null;
  onSave: (vars: { priority: TickPriority; note: string | null; targetSeason: string | null }) => void;
  onRemove: () => void;
  saving: boolean;
  error: unknown;
}) {
  const { t, labelizeT } = useT();
  const [priority, setPriority] = useState<TickPriority>(tick?.priority ?? "SOMEDAY");
  const [note, setNote] = useState(tick?.note ?? "");
  const [targetSeason, setTargetSeason] = useState(tick?.targetSeason ?? "");

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          priority,
          note: note.trim() || null,
          targetSeason: targetSeason.trim() || null,
        });
      }}
    >
      <label className="block">
        <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{t("tick.priority")}</span>
        <Select value={priority} onChange={(e) => setPriority(e.target.value as TickPriority)}>
          {TICK_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {labelizeT(p)}
            </option>
          ))}
        </Select>
      </label>
      <label className="block">
        <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{t("tick.targetSeason")}</span>
        <TextInput
          value={targetSeason}
          onChange={(e) => setTargetSeason(e.target.value)}
          placeholder={t("tick.seasonPlaceholder")}
        />
      </label>
      <label className="block">
        <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{t("tick.note")}</span>
        <TextArea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("tick.notePlaceholder")}
        />
      </label>
      {error != null && (
        <p className="text-xs text-destructive">{error instanceof Error ? error.message : String(error)}</p>
      )}
      <div className="flex items-center justify-between gap-2">
        <Button type="submit" disabled={saving} className="flex-1">
          {tick ? t("common.save") : t("tick.add")}
        </Button>
        {tick && (
          <Button
            type="button"
            variant="outline"
            onClick={onRemove}
            disabled={saving}
            className="text-muted-foreground hover:border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
          >
            {t("common.remove")}
          </Button>
        )}
      </div>
    </form>
  );
}
